import { useEffect, useRef, useState } from 'react';
import './FaqSection.css';

const FAQS = [
  {
    q: 'Do I need the official WhatsApp Business API?',
    a: 'No. Zepliq connects via a QR code scan, the same way WhatsApp Web works. You just scan once, and your bot is live. No Meta approval process, no business verification wait, no monthly API fees.',
  },
  {
    q: 'How does the AI know about my products and business?',
    a: 'You train it by uploading plain text: your product catalog, FAQs, pricing, return policy, anything. Zepliq reads it and uses it as the brain for every reply. Update it anytime and the bot reflects changes instantly.',
  },
  {
    q: 'Will it work with my Shopify or WooCommerce store?',
    a: 'Yes. Shopify connects via OAuth in seconds. WooCommerce connects via webhooks. Once connected, every new order automatically triggers a WhatsApp confirmation to the customer. No developer needed.',
  },
  {
    q: 'Can I jump in and take over a conversation from the AI?',
    a: 'Absolutely. You can take over any conversation with one click from the inbox. The AI pauses instantly and resumes only when you hand control back. Your team can also reply directly. Zepliq supports multi-agent inboxes.',
  },
  {
    q: 'What AI models does Zepliq use?',
    a: 'Zepliq supports ChatGPT (OpenAI), Google Gemini, and Claude AI. You choose which model powers your bot, or use Zepliq\'s built-in NLP engine. Switch models anytime without re-training your knowledge base.',
  },
  {
    q: 'How much does Zepliq cost?',
    a: 'Pricing depends on which channels you connect and how many conversations you handle each month, so we quote each business individually. Message us on WhatsApp with a rough idea of your volume and we will send you a price, usually within minutes. No setup fees, no contracts, cancel anytime.',
  },
  {
    q: 'Does Zepliq also handle email?',
    a: 'Yes. The AI Email Assistant connects to your Gmail account, reads incoming email, and drafts replies for your review, sending only the ones you approve. It runs on the same knowledge base as your WhatsApp automation, so you train once and it works across both channels.',
  },
  {
    q: 'Is my customer data safe?',
    a: 'All data is encrypted in transit and at rest. We never store your customer conversations beyond the retention period you set. Zepliq is SOC 2 compliant and follows GDPR data handling practices.',
  },
];

function FaqItem({ item, index }) {
  const [open, setOpen] = useState(false);
  const bodyRef = useRef(null);

  return (
    <div className={`faq__item ${open ? 'faq__item--open' : ''}`}>
      <button
        className="faq__question"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className="faq__q-text">{item.q}</span>
        <span className="faq__chevron" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </span>
      </button>
      <div
        className="faq__answer"
        ref={bodyRef}
        style={{ maxHeight: open ? bodyRef.current?.scrollHeight + 'px' : '0px' }}
      >
        <p className="faq__answer-text">{item.a}</p>
      </div>
    </div>
  );
}

export default function FaqSection() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const half = Math.ceil(FAQS.length / 2);

  return (
    <section className="faq section" ref={ref}>
      <div className="container">

        <div className={`faq__header ${visible ? 'faq__header--visible' : ''}`}>
          <span className="badge badge-brand">FAQ</span>
          <h2 className="display-lg faq__title">
            Every question,<br />
            <span className="gradient-text">answered.</span>
          </h2>
          <p className="text-lg faq__sub">
            Still have questions? <a href="/contact" className="faq__contact-link">Talk to our team →</a>
          </p>
        </div>

        <div className={`faq__grid ${visible ? 'faq__grid--visible' : ''}`}>
          <div className="faq__col">
            {FAQS.slice(0, half).map((item, i) => (
              <FaqItem key={i} item={item} index={i} />
            ))}
          </div>
          <div className="faq__col">
            {FAQS.slice(half).map((item, i) => (
              <FaqItem key={i + half} item={item} index={i + half} />
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
